import React, { useState, useEffect } from 'react';
import dayjs from 'dayjs';
import {
  Dialog,
  TextField,
  DialogContent,
  DialogContentText,
  DialogTitle,
  InputAdornment,
  Button,
  IconButton,
  Typography,
  CircularProgress,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import useSound from 'use-sound';
import alertSound from '@assets/alert_long.mp3';

import { ErrorAPI } from '@shared/components/ui/ErrorAPI';
import { useAvailabilityModalStyles } from '@shared/styles/AvailabilityModal.styles';
import { useUserInfo, useUpdateUnavailabilityEndTime } from '@shared/hooks/queries/useUserQueries';
import { getUserTimezone } from '@shared/utils/timezone';

export const AvailabilityModal = (props) => {
  const { openModal, setAvailabilityEndDate, handleCloseModal, setAlertAvailability, userId, available } = props;
  const classes = useAvailabilityModalStyles();
  const [endDate, setEndDate] = useState();
  const [duration, setDuration] = useState(20);
  const [errorAPI, setErrorAPI] = useState(false);
  const [notificationShown, setNotificationShown] = useState(false);

  const [playAlert] = useSound(alertSound, { volume: 0.25 });

  // Use TanStack Query hooks
  const { data: userInfoData, isLoading, isError } = useUserInfo(!available && openModal ? userId : null);
  const { mutate: updateEndTime } = useUpdateUnavailabilityEndTime();

  // Update state when user info is fetched
  useEffect(() => {
    if (userInfoData?.user) {
      if (userInfoData.user.available === 0) {
        const userTimezone = getUserTimezone();
        const endFormated = dayjs(userInfoData.user.unavailable_ended_at).format('YYYY-MM-DDTHH:mm');
        const nowFormated = dayjs().tz(userTimezone).format('YYYY-MM-DDTHH:mm');
        setEndDate(endFormated);
        setAvailabilityEndDate(endFormated);
        setDuration(dayjs(endFormated).diff(dayjs(nowFormated), 'minute'));
      }
    }
  }, [userInfoData, setAvailabilityEndDate]);

  // Request notification permission on component mount
  useEffect(() => {
    if (window.Notification && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, []);

  // Handle errors
  useEffect(() => {
    if (isError) {
      setErrorAPI(true);
    }
  }, [isError]);

  // Reset notification flag when user becomes available
  useEffect(() => {
    if (available) {
      setNotificationShown(false);
    }
  }, [available]);

  // Separate effect for the countdown interval
  useEffect(() => {
    if (!available && endDate) {
      const interval = setInterval(() => {
        const userTimezone = getUserTimezone();
        const nowFormated = dayjs().tz(userTimezone).format('YYYY-MM-DDTHH:mm');
        var diff = dayjs(endDate).diff(dayjs(nowFormated), 'minute');
        setDuration(diff);

        if (diff <= 2 && diff >= 0) {
          setAlertAvailability(true);
          // playAlert();
        }

        // Show notification when countdown reaches zero (only once)
        if (diff <= 0 && !notificationShown) {
          if (window.Notification && Notification.permission === 'granted') {
            new Notification('Indisponibilité terminée', {
              body: "Votre période d'indisponibilité est maintenant terminée.",
              icon: '/favicon.ico',
              requireInteraction: true,
            });
            playAlert();
            setNotificationShown(true);
          }
        }
      }, 10000);

      return () => clearInterval(interval);
    }
  }, [endDate, available, notificationShown, setAlertAvailability, playAlert]);

  const handleEndDateChange = (event) => {
    const userTimezone = getUserTimezone();
    const nowFormated = dayjs().tz(userTimezone).format('YYYY-MM-DDTHH:mm');
    const end = dayjs(event.target.value);
    var diff = end.diff(dayjs(nowFormated), 'minute');
    setDuration(diff);
    if (diff <= 2 && diff >= 0) {
      setAlertAvailability(true);
      // playAlert();
    }
    setEndDate(event.target.value);
    handleTimeChange(event.target.value);
  };

  const handleDurationChange = (event) => {
    const userTimezone = getUserTimezone();
    const newDuration = parseInt(event.target.value);
    const now = dayjs().tz(userTimezone).format('YYYY-MM-DDTHH:mm');
    var newEndDate = dayjs(now).add(newDuration, 'minute').format('YYYY-MM-DDTHH:mm');
    setEndDate(newEndDate);
    setDuration(newDuration);
    handleTimeChange(newEndDate);
  };

  const addDuration = (time) => {
    const userTimezone = getUserTimezone();
    const newDuration = duration + time;
    const now = dayjs().tz(userTimezone).format('YYYY-MM-DDTHH:mm');
    const newEndDate = dayjs(now).add(newDuration, 'minute').format('YYYY-MM-DDTHH:mm');
    setEndDate(newEndDate);
    setDuration(newDuration);
    handleTimeChange(newEndDate);
  };

  const removeDuration = (time) => {
    const userTimezone = getUserTimezone();
    const newDuration = duration - time;
    const now = dayjs().tz(userTimezone).format('YYYY-MM-DDTHH:mm');
    const newEndDate = dayjs(now).add(newDuration, 'minute').format('YYYY-MM-DDTHH:mm');
    setEndDate(newEndDate);
    setDuration(newDuration);
    handleTimeChange(newEndDate);
  };

  const handleTimeChange = (end) => {
    updateEndTime(
      {
        userId: userId,
        unavailableEndedAt: end,
      },
      {
        onError: (error) => {
          console.log(error);
          setErrorAPI(true);
        },
      }
    );
  };

  return (
    <div>
      <Dialog open={openModal} onClose={handleCloseModal} aria-labelledby="form-dialog-title">
        <DialogTitle disableTypography>
          <Typography variant="h6" className={classes.title}>
            Détail de la durée de l'activité
          </Typography>

          <IconButton aria-label="close" className={classes.closeButton} onClick={handleCloseModal}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        {!isLoading ? (
          <DialogContent dividers>
            <ErrorAPI
              errorAPI={errorAPI}
              setErrorAPI={setErrorAPI}
              message={
                'Un problème est survenu lors du chargement des données, veuillez recharger la page ou réessayer plus tard.'
              }
            />
            <div className={classes.text}>
              <DialogContentText>Statut actuel : Au téléphone</DialogContentText>
            </div>
            <div className={classes.buttonRow}>
              <TextField
                id="datetime-end"
                label="Heure de fin estimée"
                type="datetime-local"
                className={classes.textField}
                value={endDate}
                onChange={handleEndDateChange}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </div>
            <div className={classes.buttonRow}>
              <TextField
                type="number"
                value={duration}
                onChange={handleDurationChange}
                className={classes.textField}
                InputLabelProps={{
                  shrink: true,
                }}
                InputProps={{
                  inputProps: {
                    max: 100,
                    min: 0,
                  },
                  endAdornment: <InputAdornment position="end">minutes</InputAdornment>,
                }}
                label="Durée restante"
              />
            </div>
            <div className={classes.buttonRow}>
              <Button variant="outlined" onClick={() => removeDuration(5)}>
                -5 min
              </Button>
              <Button variant="outlined" className={classes.button} onClick={() => addDuration(5)}>
                + 5 min
              </Button>
            </div>
          </DialogContent>
        ) : (
          <div className={classes.buttonRow}>
            <CircularProgress />
          </div>
        )}
      </Dialog>
    </div>
  );
};
